<template>
  <main class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div class="d-flex align-items-center mb-3">
          <h1 class="h2">
            Flights from {{ data.origin.city.name }} to {{ data.destination.city.name }}
          </h1>
        </div>
        <div class="mb-2 d-flex">
          <div class="route me-4">
            <div class="step">
              <NuxtImg
                  alt="Origin icon"
                  width="11"
                  height="11"
                  src="img/arrow.up.forward.svg"
              />
            </div>
            <div class="step">
              <NuxtImg
                  alt="Destination icon"
                  width="11"
                  height="11"
                  src="img/arrow.down.forward.svg"
              />
            </div>
          </div>
          <div class="w-100">
            <div class="mb-3">
              <h2 class="mb-1 h5 small text-uppercase opacity-75">
                <NuxtLink
                    class="inherit"
                    :to="{ name: 'flights-from', params: { name: data.origin.city.slug_en, iata: data.origin.iata.toLowerCase() } }"
                    :title="'Flights from ' + data.origin.city.name">
                  {{ data.origin.city.name }}
                </NuxtLink>
              </h2>
              <p class="h3 mb-1">{{ data.origin.iata }}</p>
              <p class="mb-1 small opacity-75">{{ data.origin.name_en }}</p>
            </div>
            <div class="divider">
              <span>{{ formattedDistance }} · {{ formattedTime }}</span>
            </div>
            <div class="mt-3">
              <h2 class="mb-1 h5 small text-uppercase opacity-75">
                <NuxtLink
                    class="inherit"
                    :to="{ name: 'flights-to', params: { name: data.destination.city.slug_en, iata: data.destination.iata.toLowerCase() } }"
                    :title="'Flights to ' + data.destination.city.name">
                  {{ data.destination.city.name }}
                </NuxtLink>
              </h2>
              <p class="h3 mb-1">{{ data.destination.iata }}</p>
              <p class="mb-1 small opacity-75">{{ data.destination.name_en }}</p>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12 mt-4">
            <div class="row g-2 mb-4" v-if="promoReward">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Promo Reward (deal)</h2>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Class</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ promoReward?.cabin }}</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Airline</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <span class="d-flex align-items-center" v-if="promoReward?.airline === 'AF'">
                      <NuxtImg
                          alt="Air France Logo"
                          loading="lazy"
                          class="me-1 position-relative"
                          height="10"
                          width="19"
                          style="top: -1px"
                          src="img/af-logo.svg"/>
                      <NuxtLink
                          :to="{ name: 'airlines-slug', params: { slug: 'air-france' }}"
                          class="inherit"
                          title="Air France">Air France</NuxtLink>
                    </span>
                    <span class="d-flex align-items-center" v-if="promoReward?.airline === 'KL'">
                      <NuxtImg
                          alt="KLM Logo"
                          loading="lazy"
                          class="me-1 position-relative"
                          height="10"
                          width="19"
                          style="top: -1px"
                          src="img/klm-logo.svg"/>
                      <NuxtLink
                          :to="{ name: 'airlines-slug', params: { slug: 'klm-royal-dutch-airlines' }}"
                          class="inherit"
                          title="KLM Royal Dutch Airlines">KLM</NuxtLink>
                    </span>
                  </p>
                </div>
              </div>
              <div class="d-none d-sm-block col-sm-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Discount</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ promoReward?.discount }}%</p>
                </div>
              </div>
              <div class="col-6 col-sm-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">One Way</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ Intl.NumberFormat("en").format(promoReward?.miles) }} miles</p>
                </div>
              </div>
              <div class="col-6 col-sm-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Round trip</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ Intl.NumberFormat("en").format(promoReward?.miles * 2) }} miles</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Travel Period</h3>
                  <p class="mb-0 fw-bold mb-n1" v-if="$device.isMobile">{{ moment(promoReward.travel_date_from, "MM/DD/YYYY").format('MMM \'YY') }} - {{ moment(promoReward.travel_date_to, "MM/DD/YYYY").format('MMM \'YY') }}</p>
                  <p class="mb-0 fw-bold mb-n1" v-else>{{ moment(promoReward.travel_date_from, "MM/DD/YYYY").format('MMM YYYY') }} - {{ moment(promoReward.travel_date_to, "MM/DD/YYYY").format('MMM YYYY') }}</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Expiration</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ moment(promoReward.end_booking_date, "MM/DD/YYYY").format('D MMM') }} <small class="ms-1">({{ moment(promoReward.end_booking_date, "MM/DD/YYYY").fromNow(true)}})</small></p>
                </div>
              </div>
            </div>

            <div class="row g-2 mb-4" v-else-if="data.origin.iata !== data.destination.iata">
              <div class="col-12">
                <div class="alert alert-warning mb-0">
                  <p class="mb-0 small">There are no deals this month for flights from {{ data.origin.city.name }} to {{ data.destination.city.name }} with <a class="inherit" title="KLM" href="https://rewards.flights/airlines/klm-royal-dutch-airlines">KLM</a> or <a class="inherit" title="Air France" href="https://rewards.flights/airlines/air-france">Air France</a>.</p>
                  <p v-if="data.calculator.length > 0" class="mb-0 small">The standard prices for a KLM flight from {{ data.origin.city.name }} to {{ data.destination.city.name }} are {{ Intl.NumberFormat("en").format(data.calculator[0].miles) }} miles in {{ data.calculator[0].cabin }}.</p>
                </div>
              </div>
            </div>

            <div class="row g-2 mb-4" v-if="data.calculator.length > 0">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Standard prices</h2>
              </div>
              <div v-for="ticket in data.calculator" class="col-12 col-sm">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">{{ ticket.cabin }}</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ Intl.NumberFormat("en").format(ticket.miles) }} miles</p>
                </div>
              </div>
            </div>

            <reward-table
                v-if="data.reward_tickets && data.reward_tickets.length > 0"
                :tickets="data.reward_tickets"
                :promo-reward="promoReward"
                :airports="[data.origin.iata, data.destination.iata]"
            />

            <div class="row g-2 mb-4" v-if="historyTickets.length > 0">
              <div class="col-12">
                <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Deals history</h2>
              </div>
              <div class="col-12">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="fw-normal mb-1 small text-uppercase opacity-75">History (One Way)</h3>
                  <ul class="timeline list-unstyled mb-0">
                    <li class="step mt-2 d-flex align-items-center" v-for="(ticket, index) in historyTickets" :class="{'mb-2': index + 1 < historyTickets.length }">
                      <span class="dot me-3" />
                      <div>
                        <p class="fw-bold mb-1">{{ moment(ticket.travel_date_from, "MM/DD/YYYY").format('MMM. YYYY') }}</p>
                        <p class="small mb-0">{{ Intl.NumberFormat("nl").format(ticket.miles) }} miles · {{ ticket.cabin }}</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="row g2 mb-5" v-if="data.origin.iata === data.destination.iata">
              <div class="col-12">
                <div class="alert alert-danger mb-0 mt-n3">
                  <span class="fw-bold d-block">Really? ..</span> You want to fly to the same airport?
                </div>
                <img alt="wtf" width="200" class="rounded mw-100 mt-3" src="~/assets/img/dafuq.jpg">
              </div>
            </div>
            <div v-else class="col-12 mt-4 mb-5">
              <div class="card no-hover p-2 bg-blurry d-flex">
                <a
                    :href="generateTicketURL"
                    target="_blank"
                    :title="'Flights from ' + data.origin.city.name + ' to ' + data.destination.city.name"
                    rel="noopener nofollow"
                    class="btn bg-primary fw-bold rounded-1">
                  {{ promoReward ? 'View availability' : 'View tickets'}}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
          <div class="col-12">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Frequently Asked Questions</h2>
          </div>
          <Question>
            <template v-slot:question>
              How do I book a Promo Reward from {{ data.origin.city.name }} ({{ data.origin.iata }}) to {{ data.destination.city.name }} ({{ data.destination.iata }})?
            </template>
            <template v-slot:answer>
              <p>Booking a Promo Reward flight with KLM or Air France to {{ data.destination.city.name }} should be done through the respective airlines' websites. To help you, we've created a step-by-step guide for KLM.</p>
              <ol>
                <li>Go to the KLM website  (<a class="fw-bold" target="_blank" rel="nofollow noopener" title="Open KLM" :href="generateTicketURL">or click here</a>).</li>
                <li>Sign in with your Flying Blue account.</li>
                <li>Navigate back to the KLM homepage.</li>
                <li>Click on "Book with Miles".</li>
                <li>In the "Departure from" field, enter your starting point: {{ data.origin.city.name }} ({{ data.origin.iata }})</li>
                <li>In the "Arrival at" field, enter your destination: {{ data.destination.city.name }} ({{ data.destination.iata }})</li>
                <li>Next, select your travel period, number of passengers, travel class, and flight type (one-way or round trip).</li>
                <li>Click "Search flights" to start looking for available tickets.</li>
                <li>Choose your preferred Promo Reward flight to {{ data.destination.city.name }}.</li>
                <li>After making your selection, you'll see a summary including the total price.</li>
                <li>Finalize the payment, and your KLM / Air France flight ticket from {{ data.origin.city.name }} ({{ data.origin.iata }}) to {{ data.destination.city.name }} ({{ data.destination.iata }}) will be confirmed.</li>
              </ol>
            </template>
          </Question>
          <Question>
            <template v-slot:question>
              How many Flying Blue miles do I need for a flight from {{ data.origin.city.name }} to {{ data.destination.city.name }}?
            </template>
            <template v-slot:answer>
              <p v-if="data.calculator.length > 0">The price for a KLM Reward flight ticket from {{ data.origin.city.name }} to {{ data.destination.city.name }} starts at {{ Intl.NumberFormat("en").format(data.calculator[0].miles) }} Flying Blue miles in {{ data.calculator[0].cabin }}. Depending on the travel period you want to fly, the price may vary. Tickets that can be purchased with Flying Blue miles have a different (limited) stock than tickets purchased with euros.</p>
              <p v-if="data.tickets.length > 0">In addition to standard Reward flight tickets to {{ data.destination.city.name }}, Promo Rewards are also regularly available. These are promotional fare tickets that are temporarily available.</p>
              <p class="mb-0" v-if="data.tickets.length > 0">The latest Promo Reward from {{ data.origin.city.name }} ({{ data.origin.iata }}) to {{ data.destination.city.name }} ({{ data.destination.iata }}) was in {{ moment(data.tickets[0].travel_date_from, "MM/DD/YYYY").format('MMMM YYYY') }}. A flight ticket to {{ data.destination.city.name }} then cost {{ Intl.NumberFormat("en").format(data.tickets[0].miles) }} miles in {{ data.tickets[0].cabin }}.</p>
              <p class="mb-0" v-else>Unfortunately, we have not (yet) seen a KLM Reward flight ticket from {{ data.origin.city.name }} ({{ data.origin.iata }}) to {{ data.destination.city.name }} ({{ data.destination.iata }}). Therefore, we cannot say what a flight ticket to {{ data.destination.city.name }} costs.</p>
            </template>
          </Question>
          <Question>
            <template v-slot:question>
              When was the last Promo Reward from {{ data.origin.city.name }} to {{ data.destination.city.name }}?
            </template>
            <template v-slot:answer>
              <p v-if="promoReward">Good news! A Promo Reward is available this month. The promotional fare for this route can be seen at the top of this page.</p>
              <p v-else>Unfortunately, there is no Promo Reward available to {{ data.destination.city.name }} this month.</p>
              <p class="mb-0" v-if="promoReward && data.tickets.length > 0">
                The last known Promo Reward to us for {{ data.destination.city.name }} ({{ data.origin.iata }}) was in {{ moment(data.tickets[0].travel_date_from, "MM/DD/YYYY").format('MMMM YYYY') }}.
              </p>
              <p class="mb-0" v-if="!promoReward && !data.tickets.length > 0">We unfortunately have no history for this flight route with KLM or Air France.</p>
            </template>
          </Question>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup>
import moment from "moment";

definePageMeta({
  name: 'flights-between'
})

const runtimeConfig = useRuntimeConfig()
const route = useRoute();
const error = useError();

const { data } = await useAsyncData(
    'airports-' + route.params.iata + '-' + route.params.toiata, async () => {
      const response = await $fetch(runtimeConfig.public.backendUrl + "/api/v2/airports/multiple", {
        params: {
          'airports[]': [route.params.iata, route.params.toiata],
        },
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        }
      });

      if (response.airports.length !== 2) {
        error.value = { statusCode: 404, message: 'We unfortunately cannot find the route from ' + route.params.iata.toUpperCase() + ' to ' + route.params.toiata.toUpperCase() + '.' };
        return;
      }

      const { airports: [origin, destination], calculator, reward_tickets } = response;
      let distance, time;

      if (response.route) {
        ({ distance, time } = response.route);
      }

      const tickets = response.promo_rewards.filter((x) =>
          x.start !== null || ['ams', 'cdg', 'dus'].includes(route.params.iata)
      ).sort( ( a, b) => new Date(b.end_booking_date) - new Date(a.end_booking_date));

      return { origin, destination, tickets, calculator, reward_tickets, distance, time };
    }
);

useSeoMeta({
  appleItunesApp: {'app-id': '1672941903', 'app-clip-bundle-id': 'bjornantonissen.nl.earnonline.Clip', 'app-clip-display': 'card'},
  title: "✈ Flying Blue Reward tickets from " + data.value.origin.city.name +" to " + data.value.destination.city.name,
  ogTitle: "✈ Flying Blue Reward tickets from " + data.value.origin.city.name +" to " + data.value.destination.city.name,
  twitterTitle: "✈ Flying Blue Reward tickets from " + data.value.origin.city.name +" to " + data.value.destination.city.name,
  description: "Find cheap Air France and KLM Promo Rewards flights from " + data.value.origin.name_en +" (" + data.value.origin.iata +") to " + data.value.destination.name_en + " (" + data.value.destination.iata + ")",
  ogDescription: "Find cheap Air France and KLM Promo Rewards flights from " + data.value.origin.name_en +" (" + data.value.origin.iata +") to " + data.value.destination.name_en + " (" + data.value.destination.iata + ")",
  twitterDescription: "Find cheap Air France and KLM Promo Rewards flights from " + data.value.origin.name_en +" (" + data.value.origin.iata +") to " + data.value.destination.name_en + " (" + data.value.destination.iata + ")",
  ogUrl: runtimeConfig.public.baseUrl + "/flights-from-" + data.value.origin.city.slug_en + "-" + data.value.origin.iata.toLowerCase() + "-to-" + data.value.destination.city.slug_en + "-" + data.value.destination.iata.toLowerCase(),
  ogImage: data.value.destination.city.images.medium,
  twitterImage: data.value.destination.city.images.medium,
  twitterImageAlt: 'Photo of ' + data.value.destination.city.name,
  ogLocale: "en",
  ogLocaleAlternate: "nl",
});

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/flights-from-" + data.value.origin.city.slug_en + "-" + data.value.origin.iata.toLowerCase() + "-to-" + data.value.destination.city.slug_en + "-" + data.value.destination.iata.toLowerCase(),
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/flights-from-" + data.value.origin.city.slug_en + "-" + data.value.origin.iata.toLowerCase() + "-to-" + data.value.destination.city.slug_en + "-" + data.value.destination.iata.toLowerCase(),
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/flights-from-" + data.value.origin.city.slug_en + "-" + data.value.origin.iata.toLowerCase() + "-to-" + data.value.destination.city.slug_en + "-" + data.value.destination.iata.toLowerCase(),
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/vluchten-vanaf-" + data.value.origin.city.slug_nl + "-" + data.value.origin.iata.toLowerCase() + "-naar-" + data.value.destination.city.slug_nl + "-" + data.value.destination.iata.toLowerCase(),
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: data.value.origin.city.name,
            item: runtimeConfig.public.baseUrl + '/flights-from-' + data.value.origin.city.slug_en + '-' + data.value.origin.iata.toLowerCase(),
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.destination.city.name,
          },
        ],
      }
    ]
  }
});

const historyTickets = computed(() => data.value.tickets.filter(x => !x.active));

const promoReward = computed(() => {
  return data.value.tickets.find((x) => x.active === true);
});

if (data.value.tickets.find((x) => x.active === true)) {
  let promoReward = data.value.tickets.find((x) => x.active === true)
  useJsonld(() => {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          name: (promoReward.airline === 'AF' ? 'Air France' : 'KLM' ) + ' ticket from ' + data.value.origin.city.name + ' to ' + data.value.destination.city.name,
          description: 'Promo Rewards ticket from ' + data.value.origin.city.name + ' (' + data.value.origin.name + ') to ' + data.value.destination.city.name + ' (' + data.value.destination.name + ').',
          category: 'Travel',
          image: data.value.destination.city.images.medium,
          brand: {
            '@type': 'Brand',
            name: (promoReward.airline === 'AF' ? 'Air France' : 'KLM'),
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: data.value.destination.rating,
            ratingCount: Math.round(data.value.destination.rating_total / 100),
            bestRating: 5,
            worstRating: 1,
          },
          offers: {
            "@type": "Offer",
            price: (Intl.NumberFormat("en", { minimumFractionDigits: 2 }).format(promoReward.miles * 0.012)),
            priceCurrency: 'EUR',
            availability: 'http://schema.org/InStock',
            itemCondition: 'http://schema.org/NewCondition',
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "NL",
              "refundType": "http://schema.org/ExchangeRefund",
              "returnPolicyCategory": "http://schema.org/MerchantReturnNotPermitted",
              "restockingFee": "http://schema.org/NoRestockingFee"
            },
            priceValidUntil: moment().endOf('month').format('YYYY-MM-DD'),
            seller: {
              "@type": "Organization",
              name: "Flying Blue",
            }
          }
        }
      ]
    }
  })
}

const generateTicketURL = computed(() => {
  const cabinClass = (promoReward) => {
    if (promoReward?.cabin === 'Business'){
      return 'BUSINESS'
    }
    if (promoReward?.cabin === 'Premium Economy'){
      return 'PREMIUM'
    }
    return 'ECONOMY';
  }
  return 'https://prf.hn/click/camref:1100laQj/creativeref:1100l65362/ar:rewards.flights/pubref:'+ data.value.destination.iata +'/destination:https://www.klm.nl/en/search/open-dates?pax=1:0:0:0:0:0:0:0&activeConnection=0&cabinClass='+ cabinClass(promoReward.value) +'&connections='+ data.value.origin.iata +':A%3E'+ data.value.destination.iata +':A&bookingFlow=REWARD'
})

const formattedTime = computed(() => {
  let duration = moment.duration(data.value.time, 'minutes');
  return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
});

const formattedDistance = computed(() => {
  if(data.value.distance === undefined) {
    return
  }
  return Intl.NumberFormat("en").format(data.value.distance) + ' km';
});
</script>